<script setup>
import { ref, watch, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import {
  stripInlineBase64Images,
  toggleBlockquote,
} from 'dashboard/helper/editorHelper';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  messageSignature: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['updateSignature']);

const { t } = useI18n();
const signature = ref(props.messageSignature ?? '');
const editor = useTemplateRef('editor');
watch(
  () => props.messageSignature ?? '',
  newValue => {
    signature.value = newValue;
  }
);

// The legal text of a signature is stored as a blockquote.
const toggleLegalText = () => editor.value.runCommand(toggleBlockquote);

const updateSignature = () => {
  const { sanitizedContent, hasInlineImages } = stripInlineBase64Images(
    signature.value || ''
  );
  signature.value = sanitizedContent.trim();
  if (hasInlineImages) {
    useAlert(
      t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.INLINE_IMAGE_WARNING')
    );
  }
  emit('updateSignature', signature.value);
};
</script>

<template>
  <form class="flex flex-col gap-6" @submit.prevent="updateSignature()">
    <WootMessageEditor
      id="message-signature-input"
      ref="editor"
      v-model="signature"
      class="message-editor h-[10rem] !px-3 [&_blockquote]:!border-0 [&_blockquote]:!p-0 [&_blockquote]:!m-0 [&_blockquote_p]:!text-xs [&_blockquote_p]:!italic [&_blockquote_p]:!text-n-slate-10"
      is-format-mode
      :placeholder="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE.PLACEHOLDER')"
      channel-type="Context::MessageSignature"
      :enable-suggestions="false"
    />
    <div class="flex flex-wrap items-center gap-3">
      <NextButton
        type="button"
        slate
        faded
        sm
        icon="i-lucide-scale"
        :label="
          $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.LEGAL_TEXT.LABEL')
        "
        @mousedown.prevent
        @click="toggleLegalText"
      />
      <span class="text-sm text-n-slate-11">
        {{
          $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.LEGAL_TEXT.NOTE')
        }}
      </span>
    </div>
    <div>
      <NextButton
        type="submit"
        :label="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.BTN_TEXT')"
      />
    </div>
  </form>
</template>
